/**
 * A clinic's subscription state — STORY-190b, BL-D10, BL-D11, BL-D12.
 *
 * **Three states and no trial** (MOD-D19). **No row means active** (BL-D12), so
 * a clinic nothing has been recorded for is active. The operator moves a clinic
 * by hand; nothing here changes by itself when a date passes.
 */
export const SUBSCRIPTION_STATES = ["ACTIVE", "GRACE", "READ_ONLY"] as const;
export type SubscriptionState = (typeof SUBSCRIPTION_STATES)[number];

export function isSubscriptionState(value: string): value is SubscriptionState {
  return (SUBSCRIPTION_STATES as readonly string[]).includes(value);
}

export const SUBSCRIPTION_LABELS: Record<SubscriptionState, string> = {
  ACTIVE: "Active",
  GRACE: "In grace",
  READ_ONLY: "Read-only",
};

/** The default grace length in days (BL-D10), editable per clinic. */
export const DEFAULT_GRACE_DAYS = 14;

/** Today plus `days`, as `YYYY-MM-DD` in UTC — where grace ends if it starts today. */
export function graceEndsOn(today: string, days: number): string {
  const d = new Date(`${today}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Whole days past the due date, or 0 when it is today or ahead or there is none.
 * **Overdue is a fact about the date, not about the state**: only the operator
 * recording a payment (a new due date) clears it (BL-D10).
 */
export function daysOverdue(dueOn: string | null, today: string): number {
  if (dueOn === null || dueOn >= today) return 0;
  const ms = new Date(`${today}T00:00:00Z`).getTime() - new Date(`${dueOn}T00:00:00Z`).getTime();
  return Math.round(ms / 86_400_000);
}

/** What the clinic itself is told about its standing (STORY-190d), without any amount. */
export interface SubscriptionStanding {
  state: SubscriptionState;
  /** When grace ends, while in grace. */
  graceEndsOn: string | null;
}

/** The `code` on the 403 a read-only clinic causes, so a screen can say why (STORY-190c). */
export const SUBSCRIPTION_READ_ONLY = "SUBSCRIPTION_READ_ONLY";

/** The one sentence every refused change carries (BL-D11). */
export const READ_ONLY_MESSAGE =
  "This clinic's subscription is read-only, so everything can be read and nothing can be changed. Ask your clinic's admin to contact DentaCore.";
